import { HttpClient } from '@angular/common/http';
import { Component, HostBinding, Inject, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, ValidationErrors } from '@angular/forms';
import {
  IModalsService,
  IMODALS_SERVICE,
  INotificationsService,
  INOTIFICATIONS_SERVICE,
  P500UIBaseModal,
} from '@platform500services/p500-ui-kit';
import { filter } from 'rxjs/operators';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { AuthServiceInterface } from '../../services/auth/auth-service.interface';
import { IEnvironment } from '../../services/environment/environment.interface';
import { IUnsecuredService } from '../../services/unsecured/unsecured-service.interface';
import { Router } from '@angular/router';

@Component({
  templateUrl: './2fa-modal.component.html',
})
@DestroySubscribers()
export class Account2FAModal extends P500UIBaseModal implements OnInit {
  @HostBinding('class') className = 'c-modal c-modal--d -primary c-modal--action-form c-modal--2fa -small';

  public form: UntypedFormGroup = new UntypedFormGroup({
    telegram_id: new UntypedFormControl(null, Validators.required),
    validation_code: new UntypedFormControl(null, [
      (): ValidationErrors | null => {
        return this.verificationCodeInvalid ? { customValidate: '' } : null;
      },
    ]),
    otp: new UntypedFormControl({ value: null, disabled: true }, Validators.required),
  });
  private endpointApi: string;
  private action = 'AffiliateCrm\\Affiliate\\Edit2FA';
  private repository = 'Eloquent\\AffiliateRepository';
  private userId: number;
  private verificationCodeInvalid: boolean;
  public isLoading = true;
  public addTelegram = false;
  public validateToken = false;
  public telegramIdValidated = false;
  public validateToken2fa = false;
  @Input() public callback = (): void => {};
  @Input() public onlyEnable = false;

  subscribers: any = {};

  constructor(
    @Inject(IMODALS_SERVICE) modalsService: IModalsService,
    @Inject('IEnvironmentService') private envService: IEnvironment,
    @Inject('AuthServiceInterface') private authService: AuthServiceInterface,
    @Inject(INOTIFICATIONS_SERVICE) private notificationsService: INotificationsService,
    @Inject('IUnsecuredService') public unsecuredService: IUnsecuredService,
    private http: HttpClient,
    router: Router
  ) {
    super(modalsService, router);
    this.endpointApi = this.envService.getVar('services.api') as string;
    this.userId = this.authService.userData()?.id;
  }

  ngOnInit(): void {
    if (this.userId) {
      this.get2faData();
    }

    this.subscribers.clearInvalidCode = this.validationCode.valueChanges.subscribe((value) => {
      this.verificationCodeInvalid = false;
      if (value && `${value}`.length === 6) {
        if (this.submitButtonDisabled()) {
          this.verifyTelegramId();
        } else {
          this.onSubmit();
        }
      }
    });

    if (this.unsecuredService.isUnsecuredValue) {
      this.telegramIdValidated = true;
    }
  }

  onSubmit(close?: boolean): void {
    this.isLoading = true;
    const requestParams: any = {
      id: this.userId,
      action: this.action,
      repository: this.repository,
      arguments: {
        ...this.form.getRawValue(),
      },
    };
    this.http.post(`${this.endpointApi}/action/process`, requestParams).subscribe(() => {
      this.notificationsService.add(
        {
          type: 'success',
          title: $localize`:@@common.success:Success`,
        },
        true
      );
      if (close) {
        this.close();
      }
      this.addTelegram = false;
      this.otp.enable();
      this.authService.getUser().subscribe(() => {
        if (!this.otp.value) {
          this.unsecuredService.leave();
        }
        if (close) {
          this.callback();
        }
      });
      this.isLoading = false;
    });
  }

  verifyTelegramId(resend?: boolean): void {
    this.isLoading = true;
    const token = this.validationCode.value && !resend ? this.validationCode.value : '';
    this.http
      .post(`${this.endpointApi}/unsecured/enable`, {
        telegram_id: this.telegram.value,
        token_2fa: token,
      })
      .subscribe(
        () => {
          if (!this.validationCode.value || resend) {
            this.validateToken = true;
          } else {
            this.onSubmit();
            this.telegramIdValidated = true;
            this.validateToken = false;
            this.validateToken2fa = false;
            this.validationCode.setValue('');
          }
          this.isLoading = false;
        },
        () => {
          if (!this.validationCode.value || resend) {
            this.validateToken = true;
          } else {
            this.verificationCodeInvalid = true;
          }
          this.isLoading = false;
        }
      );
  }

  returnToTelegramId(): void {
    this.validateToken = false;
    this.validationCode.reset();
  }

  get2faData(): void {
    const requestParams = {
      id: this.userId,
      action: this.action,
      arguments: {},
      repository: this.repository,
    };
    this.http
      .post<any>(`${this.endpointApi}/action/data`, requestParams)
      .pipe(
        filter((data) => !!data?.data),
        filter(({ data }) => !!data?.fields)
      )
      .subscribe(({ data: { fields } }) => {
        this.form.patchValue(
          ((fields as any[]) || []).reduce((acc, { name, value }) => {
            acc[name] = value;
            return acc;
          }, {})
        );
        if (this.telegram.value) {
          this.otp.enable();
        }
        this.isLoading = false;
      });
  }

  handleChange(isEnabled: number): void {
    if (+isEnabled && !this.telegramIdValidated) {
      this.verifyTelegramId();
      this.validateToken2fa = true;
    } else {
      this.validateToken2fa = false;
    }
  }

  public submitButtonDisabled(): boolean {
    return (
      !this.telegram.value || this.addTelegram || (!this.telegramIdValidated && !!+this.otp.value) || (this.onlyEnable && !+this.otp.value)
    );
  }

  public editTelegram(): void {
    this.otp.disable();
    this.validateToken2fa = false;
    this.addTelegram = true;
  }

  get telegram() {
    return this.form.get('telegram_id');
  }

  get validationCode() {
    return this.form.get('validation_code');
  }

  get otp() {
    return this.form.get('otp');
  }
}
